import React, { useState, useRef } from 'react';
import { MentionUser } from '../types';
import { useTranslation } from '../hooks/useTranslation';
import { useMentionInput } from '../hooks/useMentionInput';
import MentionAutocomplete from './MentionAutocomplete';
import { AttachFileIcon, XIcon, SendIcon } from './playerIcons';

interface ReplyFormProps {
  onSubmit: (text: string, file?: File) => Promise<void>;
  onCancel: () => void;
  users?: MentionUser[];
}

const ReplyForm: React.FC<ReplyFormProps> = ({ onSubmit, onCancel, users = [] }) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showMentionAutocomplete, setShowMentionAutocomplete] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaContainerRef = useRef<HTMLDivElement>(null);

  const {
    text,
    setText,
    cursorPosition,
    setCursorPosition,
    handleTextChange,
    handleKeyDown: handleMentionKeyDown,
    insertMention,
    textareaRef,
  } = useMentionInput({
    users,
    onMentionSelect: () => setShowMentionAutocomplete(false),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) {
      setError(t('player.replyPlaceholder'));
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit(text.trim(), file || undefined);
      setText('');
      setFile(null);
    } catch (err) {
      setError(String(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.size > 50 * 1024 * 1024) {
        setError(t('player.fileTooLarge', { max: 50 }));
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    handleTextChange(e);
    const newText = e.target.value;
    const pos = e.target.selectionStart;
    let start = pos - 1;
    while (start >= 0 && newText[start] !== '@' && newText[start] !== ' ' && newText[start] !== '\n') {
      start--;
    }
    setShowMentionAutocomplete(start >= 0 && newText[start] === '@');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    handleMentionKeyDown(e);
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-900 rounded-lg p-3 space-y-2">
      <div ref={textareaContainerRef} className="relative">
        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleTextareaChange}
          onKeyDown={handleKeyDown}
          onSelect={(e) => setCursorPosition((e.target as HTMLTextAreaElement).selectionStart)}
          placeholder={t('player.replyPlaceholder')}
          className="w-full bg-gray-800 text-gray-200 placeholder-gray-500 rounded px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
          rows={3}
          disabled={isSubmitting}
          autoFocus
        />
        {showMentionAutocomplete && users.length > 0 && (
          <MentionAutocomplete
            text={text}
            cursorPosition={cursorPosition}
            users={users}
            onSelect={(id, name) => {
              insertMention(id, name);
              setShowMentionAutocomplete(false);
            }}
            onClose={() => setShowMentionAutocomplete(false)}
            containerRef={textareaContainerRef}
          />
        )}
      </div>

      {file && (
        <div className="flex items-center gap-2 bg-gray-800 rounded px-3 py-2">
          <AttachFileIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="text-sm text-gray-300 truncate flex-1">{file.name}</span>
          <button type="button" onClick={() => setFile(null)} className="p-1 text-gray-500 hover:text-red-400">
            <XIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {error && <div className="text-xs text-red-400 bg-red-900/20 rounded px-3 py-2">{error}</div>}

      <div className="flex items-center justify-between gap-2">
        <div>
          <input ref={fileInputRef} type="file" onChange={handleFileSelect} className="hidden" />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isSubmitting}
            className="p-2 text-gray-400 hover:text-indigo-400 hover:bg-gray-800 rounded"
            title={t('player.attachFile')}
          >
            <AttachFileIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-3 py-1.5 text-sm text-gray-400 hover:text-gray-300 hover:bg-gray-800 rounded"
          >
            {t('player.cancel')}
          </button>
          <button
            type="submit"
            disabled={!text.trim() || isSubmitting}
            className="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-500 disabled:opacity-50 flex items-center gap-2"
          >
            <SendIcon className="w-4 h-4" />
            <span>{t('player.reply')}</span>
          </button>
        </div>
      </div>
    </form>
  );
};

export default ReplyForm;
